/**
 * useElementSelection Hook
 * Task 5.2: Element Selection UI
 *
 * Manages element selection mode for commenting:
 * - Activates/deactivates selection mode
 * - Tracks hovered elements
 * - Captures clicked elements
 * - Prevents default click behavior during selection
 * - Filters out SDK elements and unsuitable targets
 */

import { useEffect, useRef } from 'react';
import { captureFingerprint } from '../lib/fingerprint';
import { generateDescription } from '../lib/description';
import type { ElementFingerprint } from '../lib/types';

export interface ElementSelectionData {
  element: Element;
  fingerprint: ElementFingerprint;
  description: string;
  clickPosition: { x: number; y: number };
}

export interface UseElementSelectionOptions {
  onElementSelected: (data: ElementSelectionData) => void;
  excludeSelector?: string;
  isActive: boolean; // Controlled externally via prop
}

export interface UseElementSelectionReturn {
  // Hook manages everything internally now
}

/**
 * Determines if an element should be ignored for selection
 */
function shouldIgnoreElement(element: Element): boolean {
  // Ignore SDK elements
  if (element.hasAttribute('data-multidev-sdk')) {
    return true;
  }

  // Ignore body and html
  const tagName = element.tagName.toLowerCase();
  if (tagName === 'body' || tagName === 'html') {
    return true;
  }

  // Check if element or any parent has SDK marker
  let current: Element | null = element;
  while (current) {
    if (current.hasAttribute('data-multidev-sdk')) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
}

export function useElementSelection(
  options: UseElementSelectionOptions
): UseElementSelectionReturn {
  const { onElementSelected, excludeSelector, isActive } = options;

  // Use refs to avoid stale closures in event listeners
  const callbackRef = useRef(onElementSelected);
  const excludeSelectorRef = useRef(excludeSelector);

  // Keep refs in sync
  useEffect(() => {
    callbackRef.current = onElementSelected;
  }, [onElementSelected]);

  useEffect(() => {
    excludeSelectorRef.current = excludeSelector;
  }, [excludeSelector]);

  // Single useEffect for ALL element selection behavior
  // This includes: listeners, highlighting, cursor changes
  useEffect(() => {
    if (!isActive) {
      return;
    }

    let currentHighlightedElement: HTMLElement | null = null;

    // Handle mouseover to highlight elements
    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target || shouldIgnoreElement(target)) {
        return;
      }

      if (excludeSelectorRef.current && target.matches(excludeSelectorRef.current)) {
        return;
      }

      // Remove previous highlight
      if (currentHighlightedElement && currentHighlightedElement !== target) {
        currentHighlightedElement.removeAttribute('data-multidev-highlighted');
      }

      // Add new highlight
      currentHighlightedElement = target as HTMLElement;
      currentHighlightedElement.setAttribute('data-multidev-highlighted', 'true');
    };

    // Handle mouseout to clear highlight
    const handleMouseOut = () => {
      if (currentHighlightedElement) {
        currentHighlightedElement.removeAttribute('data-multidev-highlighted');
        currentHighlightedElement = null;
      }
    };

    // Handle click to select element
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target || shouldIgnoreElement(target)) {
        return;
      }

      // Check composed path for Shadow DOM boundaries
      // This ensures clicks inside the sidebar (which is in Shadow DOM) are ignored
      const path = event.composedPath() as Element[];
      for (const element of path) {
        if (element instanceof Element && element.hasAttribute?.('data-multidev-sdk')) {
          return; // Ignore clicks on SDK elements in Shadow DOM
        }
      }

      if (excludeSelectorRef.current && target.matches(excludeSelectorRef.current)) {
        return;
      }

      // Prevent default click behavior
      event.preventDefault();
      event.stopPropagation();

      // Generate fingerprint and description
      const fingerprint = captureFingerprint(target);
      const description = generateDescription(target);

      // Invoke callback with click position
      callbackRef.current({
        element: target,
        fingerprint,
        description,
        clickPosition: { x: event.clientX, y: event.clientY },
      });

      // Clear highlight after selection
      if (currentHighlightedElement) {
        currentHighlightedElement.removeAttribute('data-multidev-highlighted');
        currentHighlightedElement = null;
      }
    };

    // Add listeners to document for capture phase
    document.addEventListener('mouseover', handleMouseOver, true);
    document.addEventListener('mouseout', handleMouseOut, true);
    document.addEventListener('click', handleClick, true);

    // Change cursor style
    const originalCursor = document.body.style.cursor;
    document.body.style.cursor = 'crosshair';

    // Cleanup function
    return () => {
      document.removeEventListener('mouseover', handleMouseOver, true);
      document.removeEventListener('mouseout', handleMouseOut, true);
      document.removeEventListener('click', handleClick, true);

      // Restore cursor
      document.body.style.cursor = originalCursor;

      // Remove any remaining highlight
      if (currentHighlightedElement) {
        currentHighlightedElement.removeAttribute('data-multidev-highlighted');
      }
    };
  }, [isActive]); // Only depends on isActive

  return {};
}
